import { useState, useEffect } from 'react';
import { MessageCircle, Check } from 'lucide-react';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

export const WhatsAppSettingsPage = () => {
  const { user, updateUser } = useAuthStore();
  const [number, setNumber] = useState(user?.phone || '');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setNumber(r.data.data.whatsappNumber || r.data.data.phone || '');
      setEnabled(r.data.data.notifyWhatsapp || false);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/whatsapp/preference', { whatsappNumber: number, notifyWhatsapp: enabled });
      toast.success('WhatsApp preferences saved!');
    } catch { toast.error('Could not save preferences'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
          <MessageCircle className="h-5 w-5 text-green-600" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">WhatsApp Notifications</h1>
          <p className="text-sm text-gray-500">Get order updates on WhatsApp</p>
        </div>
      </div>

      <div className="bg-white rounded-card shadow-card p-6 space-y-5">
        <Input
          label="WhatsApp Number"
          placeholder="+91XXXXXXXXXX"
          value={number}
          onChange={e => setNumber(e.target.value)}
        />

        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={() => setEnabled(!enabled)}
            className={`w-12 h-6 rounded-full transition-colors relative ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? 'left-7' : 'left-1'}`} />
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm">Enable WhatsApp notifications</p>
            <p className="text-xs text-gray-400">Receive order confirmations, shipping updates & pickup alerts</p>
          </div>
        </label>

        {enabled && (
          <div className="bg-green-50 rounded-xl p-4 text-sm text-green-800 space-y-2">
            <p className="font-semibold">You'll receive WhatsApp messages for:</p>
            {['Order confirmed', 'Order ready for pickup', 'Order shipped', 'Out for delivery'].map(m => (
              <div key={m} className="flex items-center gap-2"><Check className="h-4 w-4" /> {m}</div>
            ))}
          </div>
        )}

        <Button className="w-full" loading={saving} onClick={save}>Save Preferences</Button>
      </div>
    </div>
  );
};
